"use client";

/**
 * Public schedule data provider.
 *
 * Fetches the event's schedule items, config, and labels ONCE for the public view and exposes
 * them — plus pre-resolved live-time instants — through context. Both the Schedule section
 * (`ScheduleRenderer`) and the sticky live banner (`ScheduleLiveBanner`) read from here, so the
 * data is fetched a single time and they never disagree about "what's happening now".
 *
 * Holds no clock/ticking state: consumers that need the current time call `useNow` locally, so a
 * tick re-renders only that consumer, not this provider or its sibling page sections.
 */

import type React from "react";
import { createContext, useContext, useEffect, useMemo, useState } from "react";

import { useBuilderContext } from "../../_hooks/useBuilderContext";
import {
  getPublicCalendarLinkPublic,
  getScheduleConfigPublic,
  listScheduleItemsPublic,
  listScheduleLabelsPublic,
} from "../../_logic/actions/public-actions";
import type { ScheduleConfigRow, ScheduleItemRow, ScheduleLabelRow } from "../../_logic/types/db-types";
import type { PublicScheduleInitialData } from "../../_logic/types/public-page-data";
import { buildTimedItems, type TimedScheduleItem } from "../../_logic/utils/schedule-utils";

/** Shape of the public schedule data shared across the Schedule section and the live banner. */
export interface SchedulePublicData {
  /**
   * Whether schedule data is being fetched for this page. True whenever the page contains the
   * Schedule section (the section always needs its data) or opts into the site-wide live banner.
   * Gates the one-shot fetch below — NOT the live banner's visibility (see {@link bannerEnabled}).
   */
  enabled: boolean;
  /**
   * Whether the live banner should render on this page. Decided server-side from the family's master
   * live-banner switch plus the in-page/site-wide policy. Distinct from {@link enabled}: a page with
   * the Schedule section fetches data (`enabled` true) but shows the banner only when this is true.
   */
  bannerEnabled: boolean;
  items: ScheduleItemRow[];
  config: ScheduleConfigRow | null;
  labels: ScheduleLabelRow[];
  /** True while the one-shot fetch is in flight. */
  isLoading: boolean;
  /** True once the fetch has succeeded at least once. */
  loaded: boolean;
  /**
   * Items resolved to absolute start/end instants and sorted ascending, for live-status math.
   * Empty when the event has no time zone (live UI is hidden in that case).
   */
  timedItems: TimedScheduleItem[];
  /**
   * Public URL (with `#anchor`) of the Schedule section for the pre-event banner's "View schedule"
   * CTA. An in-page `#anchor` when the section is on the current page, a `/{slug}[/sub]#anchor`
   * cross-page link otherwise, or null when the family has no Schedule section. Resolved server-side.
   */
  scheduleHref: string | null;
  /** The event's public "Add this calendar" link, or null when no calendar exists yet or it isn't
   *  public (the admin's "Shareable via link" toggle is off). */
  publicCalendarUrl: string | null;
}

const SchedulePublicDataContext = createContext<SchedulePublicData | null>(null);

/**
 * Read the shared public schedule data.
 *
 * @returns The context value, or null when called outside the provider (e.g. builder mode).
 */
export function useSchedulePublicData(): SchedulePublicData | null {
  return useContext(SchedulePublicDataContext);
}

interface SchedulePublicDataProviderProps {
  /** When false, skips the fetch entirely (e.g. the page has no schedule section). */
  enabled: boolean;
  /** Whether the live banner should render here. Forwarded to context; doesn't affect the fetch. */
  bannerEnabled: boolean;
  /** Server-resolved "View schedule" CTA href for the pre-event banner, or null when none exists. */
  scheduleHref: string | null;
  /**
   * Server-fetched schedule data. When present, seeds state so the section paints on first render
   * and the one-shot fetch is skipped. Null in the builder / when the RSC didn't prefetch (a fetch
   * fallback then runs on mount).
   */
  initialData?: PublicScheduleInitialData | null;
  children: React.ReactNode;
}

/**
 * Provides public schedule data to its subtree. Mount inside the public BuilderContext so it can
 * read `eventId` / `parentPageId` / `timeZone`.
 *
 * @param props - See SchedulePublicDataProviderProps.
 * @returns The context provider wrapping `children`.
 */
export function SchedulePublicDataProvider({
  enabled,
  bannerEnabled,
  scheduleHref,
  initialData = null,
  children,
}: SchedulePublicDataProviderProps): React.ReactElement {
  // Hooks
  const { eventId, parentPageId, timeZone } = useBuilderContext();

  // States — seeded from the server prefetch when present, so the section paints without a fetch.
  const [items, setItems] = useState<ScheduleItemRow[]>(() => initialData?.items ?? []);
  const [config, setConfig] = useState<ScheduleConfigRow | null>(() => initialData?.config ?? null);
  const [labels, setLabels] = useState<ScheduleLabelRow[]>(() => initialData?.labels ?? []);
  const [publicCalendarUrl, setPublicCalendarUrl] = useState<string | null>(
    () => initialData?.publicCalendarUrl ?? null
  );
  // With server data in hand there's nothing to load; the effect's `loaded` guard then no-ops.
  const [isLoading, setIsLoading] = useState<boolean>(enabled && initialData == null);
  const [loaded, setLoaded] = useState<boolean>(initialData != null);

  // Effects — one-shot fetch of items/config/labels/calendar link
  useEffect(() => {
    if (!enabled || loaded || !isLoading) return;
    let cancelled = false;
    Promise.all([
      listScheduleItemsPublic(eventId),
      getScheduleConfigPublic(parentPageId),
      listScheduleLabelsPublic(eventId),
      getPublicCalendarLinkPublic(eventId),
    ])
      .then(([nextItems, nextConfig, nextLabels, nextPublicCalendarUrl]) => {
        if (cancelled) return;
        setItems(nextItems);
        setConfig(nextConfig);
        setLabels(nextLabels);
        setPublicCalendarUrl(nextPublicCalendarUrl);
        setLoaded(true);
        setIsLoading(false);
      })
      .catch((error) => {
        if (cancelled) return;
        console.error("SchedulePublicDataProvider: failed to load public schedule data", error);
        // Settle into a loaded-but-empty state so consumers stop waiting: the section falls back to
        // its empty render and the live banner resolves to nothing instead of a stuck skeleton.
        setLoaded(true);
        setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, loaded, isLoading, eventId, parentPageId]);

  // Derived values — resolve instants once per items/timeZone change (not per clock tick)
  const timedItems = useMemo(() => (timeZone ? buildTimedItems(items, timeZone) : []), [items, timeZone]);

  // When disabled (no schedule section), report a settled empty state so consumers — notably the
  // live banner — don't sit on a loading skeleton waiting for a fetch that never runs.
  const value = useMemo<SchedulePublicData>(
    () => ({
      enabled,
      bannerEnabled,
      items,
      config,
      labels,
      isLoading: enabled ? isLoading : false,
      loaded: enabled ? loaded : true,
      timedItems,
      scheduleHref,
      publicCalendarUrl,
    }),
    [enabled, bannerEnabled, items, config, labels, isLoading, loaded, timedItems, scheduleHref, publicCalendarUrl]
  );

  // Render
  return <SchedulePublicDataContext.Provider value={value}>{children}</SchedulePublicDataContext.Provider>;
}
